/**
 * @module Views/PartnerDashboard/PartnerIncompleteWillsPage
 *
 * This module defines the `PartnerIncompleteWillsPage` component which displays a list of incomplete wills.
 * It provides functionality for sorting, filtering, and paginating the list of incomplete wills.
 * The data is fetched asynchronously from the `fetchIncompleteWills` API service, and the page is rendered with
 * Material UI components such as tables and pagination controls.
 *
 * @example
 * // Usage:
 * import PartnerIncompleteWillsPage from 'path/to/PartnerIncompleteWillsPage';
 *
 * <PartnerIncompleteWillsPage />
 */
import { Box, Table, TableBody, TableContainer, Typography } from '@mui/material';
import { CustomTableCell, CustomTablePagination, CustomTableRow } from 'components/DesignSystem/Table/CustomTable';
import { filter } from 'lodash';
import Scrollbar from 'oldLayouts/components/scrollbar';
import { UserListHead, UserListToolbar } from 'oldLayouts/components/user';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchIncompleteWills } from 'services/partnerService';
import PageContainer from 'views/dashboard/PageContainer';

/**
 * Comparator function to sort elements in descending order based on a property.
 *
 * @param {Object} a - The first element to compare.
 * @param {Object} b - The second element to compare.
 * @param {string} orderBy - The property to compare the values of.
 *
 * @returns {number} A negative number if `a[orderBy]` is less than `b[orderBy]`,
 * a positive number if `a[orderBy]` is greater than `b[orderBy]`, and 0 if they are equal.
 */
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

/**
 * Returns a comparator function based on the order and the property to sort by.
 *
 * @param {string} order - The current sort order ('asc' or 'desc').
 * @param {string} orderBy - The property to compare the values of.
 *
 * @returns {function} A comparator function that can be used to sort an array.
 */
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Sorts and filters an array based on the given comparator and search query.
 *
 * @param {Array} array - The array to be sorted and filtered.
 * @param {function} comparator - The comparator function used to sort the array.
 * @param {string} query - The search query to filter the data.
 *
 * @returns {Array} The sorted and filtered array.
 */
function stableSort(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        JSON.stringify(_user ?? {})
          .toLowerCase()
          .indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

/**
 * Constant defining the table headers for the incomplete wills table. Each object defines the column's label, id,
 * and whether the column is numeric.
 *
 * @constant {Array} TABLE_HEAD
 * @default
 */
const TABLE_HEAD = [
  {
    id: 'email',
    numeric: true,
    label: 'Email',
    alignRight: false,
  },
  {
    id: 'name',
    disablePadding: true,
    label: 'Client Name',
    alignRight: false,
  },
  {
    id: 'phone',
    numeric: true,
    label: 'Phone',
    alignRight: false,
  },
  {
    id: 'role',
    disablePadding: true,
    label: 'Role',
    alignRight: false,
  },
];

/**
 * `PartnerIncompleteWillsPage` is a React component that displays the list of incomplete wills.
 * It handles sorting, filtering, and pagination of the data fetched from the API.
 *
 * @component
 * @example
 * // Usage example:
 * return <PartnerIncompleteWillsPage />;
 *
 * @returns {JSX.Element} The rendered component for displaying incomplete wills.
 */
const PartnerIncompleteWillsPage = () => {
  const [order, setOrder] = useState('asc'); // State for sort order
  const [orderBy, setOrderBy] = useState('email'); // State for property to sort by
  const [page, setPage] = useState(0); // State for current page number
  const [rowsPerPage, setRowsPerPage] = useState(5); // State for rows per page in pagination
  const [incompleteWillsData, setIncompletedWillsData] = useState([]); // State for storing incomplete wills data
  const [filterName, setFilterName] = useState(''); // State for managing filter input text

  /**
   * Fetches incomplete wills data from the server and updates the `incompleteWillsData` state.
   *
   * @async
   * @returns {void}
   */
  const fetchInCompleteWillsData = async () => {
    try {
      const response = await fetchIncompleteWills(true);
      const { incompleteWillsList } = response.data;
      setIncompletedWillsData(incompleteWillsList || []);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  useEffect(() => {
    // Fetch incomplete wills data when the component is mounted
    fetchInCompleteWillsData();
  }, []);

  /**
   * Handles sorting when a column header is clicked. It updates the `order` and `orderBy` state variables.
   *
   * @param {object} event - The event object from the click event.
   * @param {string} property - The property to sort the data by.
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Handles page change when the user navigates through the pagination.
   *
   * @param {object} event - The event object triggered when the page is changed.
   * @param {number} newPage - The new page number.
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handles rows per page change when the user adjusts the rows per page in the pagination.
   *
   * @param {object} event - The event object triggered when rows per page is changed.
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Calculates the number of empty rows to display when the table does not fully fill the page.
   *
   * @constant {number} emptyRows
   * @default
   */
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - incompleteWillsData.length) : 0;

  /**
   * Filters and sorts the `incompleteWillsData` array based on the search query, sorting order, and pagination.
   *
   * @constant {Array} visibleRows
   * @default
   * @description This memoized value contains the sorted and filtered data for the current page.
   */
  const visibleRows = useMemo(
    () =>
      stableSort(incompleteWillsData || [], getComparator(order, orderBy), filterName).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, incompleteWillsData, filterName]
  );

  /**
   * Handles filtering by client name when the user types in the filter input.
   * It resets the pagination to the first page.
   *
   * @param {object} event - The event object triggered by the user typing in the filter input.
   */
  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  return (
    <PageContainer>
      <Helmet>
        <title> Incomplete Wills | Get Will Done </title>
      </Helmet>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography variant="title1">Incomplete Wills</Typography>
        <Box sx={{ margin: '24px 0 12px 0' }}>
          <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />
        </Box>
      </Box>
      <Box>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table aria-labelledby="tableTitle">
              <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
              <TableBody>
                {visibleRows.map((row, index) => (
                  <CustomTableRow hover tabIndex={-1} key={index}>
                    <CustomTableCell>{row.email}</CustomTableCell>
                    <CustomTableCell>{row.name}</CustomTableCell>
                    <CustomTableCell>{row.phone}</CustomTableCell>
                    <CustomTableCell>{row.role}</CustomTableCell>
                  </CustomTableRow>
                ))}
                {emptyRows > 0 && (
                  <CustomTableRow
                    style={{
                      height: 53 * emptyRows,
                    }}
                  >
                    <CustomTableCell colSpan={6} />
                  </CustomTableRow>
                )}
              </TableBody>
              {incompleteWillsData.length === 0 && (
                <TableBody>
                  <CustomTableRow>
                    <CustomTableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      No Wills Data Found
                    </CustomTableCell>
                  </CustomTableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <CustomTablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={incompleteWillsData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </PageContainer>
  );
};

export default PartnerIncompleteWillsPage;
